import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import ConfirmModal from "../../components/ui/Modal/ConfirmModal.jsx";
import FormField from "../../components/forms/FormField/FormField.jsx";
import TextInput from "../../components/forms/TextInput/TextInput.jsx";
import Select from "../../components/forms/Select/Select.jsx";
import StatusBadge from "../../components/ui/StatusBadge/StatusBadge.jsx";
import { useApp } from "../../hooks/useApp.js";
import { listFilings, deleteFiling } from "../../services/filingsService.js";
import { getReturnPdf, getSchedule1Pdf } from "../../services/efileService.js";
import { DRAFT_STATUSES, getMergedStatus } from "../../utils/filingStatus.js";
import { payNowLink } from "../../utils/paymentMethods.js";
import { resolveStepIndex, seedReturnIntoContext } from "../../utils/openReturnAtStep.js";
import RejectionErrorModal from "../../components/ui/Modal/RejectionErrorModal.jsx";
import FeedbackPanel from "../../components/feedback/FeedbackPanel.jsx";
import { getPendingFeedback } from "../../services/engagementService.js";
import { useReferralCode } from "../../hooks/useReferralCode.js";

const PAGE_SIZE = 10;

// Matches the backend's status filter bucket vocabulary exactly
// (TaxReturnController: "status is one of all|draft|submitted|pending|accepted|rejected").
// No "Failed" option: efile_queue_2290.errorMessage is the last polling attempt's raw
// error, not a final-outcome field (non-null on most rows regardless of eventual
// Accepted/Rejected result), and getMergedStatus below has no "failed" badge for it to
// correspond to - there's no reliable signal in the data model to filter on yet.
const STATUS_FILTER_OPTIONS = [
  ["all", "All"],
  ["draft", "Draft"],
  ["submitted", "Submitted"],
  ["pending", "Pending with IRS"],
  ["accepted", "Filed"],
  ["rejected", "Rejected"],
];

const MyFilings = () => {
  const navigate = useNavigate();
  const { dispatch, showToast } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [sch1LoadingId, setSch1LoadingId] = useState(null);
  // Rating prompt shown right after a stamped Schedule 1 download - the moment the filer
  // has the result in hand. Only for a return that hasn't been rated or closed before.
  const [ratePrompt, setRatePrompt] = useState(null); // { returnId, businessName }
  const refCode = useReferralCode(!!ratePrompt);
  const [openingId, setOpeningId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [viewErrorRow, setViewErrorRow] = useState(null); // the row "View Error" was clicked for

  // Filter bar state - searchInput is what the user is typing; search is the
  // debounced value actually sent to the backend, so every keystroke doesn't
  // fire its own request.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Any filter change starts back at page 0 - a stale page number from a
  // wider result set could otherwise land past the end of a narrower one.
  useEffect(() => {
    setPage(0);
  }, [search, status, dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listFilings({ search, status, from: dateFrom, to: dateTo, page, size: PAGE_SIZE })
      .then((r) => {
        if (cancelled) return;
        setRows(r.rows);
        setTotalElements(r.totalElements);
        setTotalPages(r.totalPages);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setTotalElements(0);
          setTotalPages(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [search, status, dateFrom, dateTo, page]);

  // Loads this exact return_id's full detail (business, filing type, trucks,
  // credits, etc.) into context before entering the wizard, rather than
  // letting Wizard.jsx's own fetch effect guess at "whatever this business's
  // current draft is" (getReturnForBusiness) - that guess is only right for
  // a business's single active draft, and silently loses the saved data (or
  // auto-creates a brand-new blank draft) for anything else, including a
  // rejected return, which isn't a draft at all. Fetching by row.id here -
  // the same call viewFiling/getReturnById uses - is unambiguous regardless
  // of status, so Continue always resumes the exact return the user clicked,
  // rejected ones included (letting them fix the error and resubmit).
  const openFiling = async (row) => {
    setOpeningId(row.id);
    try {
      // A rejected return has nothing left to fill in on Form (business/
      // filing-type info was already accepted structurally - the IRS reject
      // is about the actual content), so Continue skips straight to the step
      // that holds the thing the filer needs to fix: the corrected-VIN list
      // for a VIN Correction return, the TGW worksheet for a Weight-increase
      // amended return, the mileage-exceeded vehicle list for that amend
      // reason, or the vehicle list itself for an Original/Final return -
      // never the read-only-feeling Review step, which only summarizes data
      // that lives on one of those pages.
      if (getMergedStatus(row) === "rejected") {
        const r = await seedReturnIntoContext(row, dispatch);
        const fixStepKey =
          r.returnType === "vincorrection"
            ? "vinCorrection"
            : r.returnType === "amended"
            ? r.amendReason === "weight"
              ? "amendDetail"
              : "amendedReturn"
            : "trucks";
        const target = resolveStepIndex(r, fixStepKey);
        dispatch({ type: "GO_STEP", step: target });
        navigate(`/wizard/${target}`);
        return;
      }

      // Opening an existing return - not a blank "New Filing" - already has
      // real progress, so the Form step shouldn't get the bare, stepper-free
      // landing treatment (Wizard.jsx only hides the stepper there while
      // state.maxStep hasn't passed position 1 yet). Bumping it here via the
      // same GO_STEP high-water-mark mechanism Wizard.jsx itself uses keeps
      // the stepper visible from the first click, even though the URL always
      // opens at /wizard/1 so Form's already-saved values are re-shown.
      await seedReturnIntoContext(row, dispatch);
      dispatch({ type: "GO_STEP", step: 2 });
      navigate("/wizard/1");
    } catch (err) {
      showToast(err.message || "Couldn't open this filing. Please try again.");
    } finally {
      setOpeningId(null);
    }
  };

  // Opens a read-only, full-detail look at this exact return_id (business,
  // trucks, credits, etc.) - submitted/accepted/rejected returns are
  // immutable (TaxReturn.isDraft() on the backend excludes all three), so
  // there's no wizard-editing path for them, but the same Review layout
  // still works to show what was filed. Wizard.jsx fetches the full detail
  // itself (GET /returns/{returnId}) once it mounts, so nothing needs to be
  // seeded into context here.
  const viewFiling = (row) => {
    navigate(`/wizard/review/${row.id}`);
  };

  // GET /returns/{returnId}/pdf (efileService.js) - only meaningful once the
  // IRS has accepted the return and stamped Schedule 1 exists.
  const downloadPdf = async (row) => {
    setPdfLoadingId(row.id);
    try {
      const { blob, filename } = await getReturnPdf(row.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showToast(err.message || "Couldn't download the return's PDF. Please try again.");
    } finally {
      setPdfLoadingId(null);
    }
  };

  // GET /returns/{returnId}/schedule1-pdf (efileService.js) - the actual
  // IRS-stamped Schedule 1, fetched out-of-band by the scheduler once the
  // return is accepted. Lags acceptance itself, so it can be briefly
  // unavailable even once mergedStatus is "accepted" (see row.schedule1Available).
  const downloadSchedule1 = async (row) => {
    setSch1LoadingId(row.id);
    try {
      const { blob, filename } = await getSchedule1Pdf(row.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      // Ask for a rating once the download has started. A failure here never affects the
      // download itself - the filer just isn't asked this time.
      getPendingFeedback(row.id)
        .then((p) => {
          if (p?.returnId) setRatePrompt({ returnId: p.returnId, businessName: p.businessName });
        })
        .catch(() => {});
    } catch (err) {
      showToast(err.message || "Couldn't download the Schedule 1 PDF. Please try again.");
    } finally {
      setSch1LoadingId(null);
    }
  };

  // DELETE /returns/{returnId} (filingsService.js) - draft-only; the backend
  // 409s if the return has already been submitted.
  const removeFiling = (row) => {
    setDeleteError("");
    setPendingDelete(row);
  };

  const cancelDeleteFiling = () => {
    setPendingDelete(null);
    setDeleteError("");
  };

  const confirmDeleteFiling = async () => {
    const row = pendingDelete;
    if (!row) return;
    setDeletingId(row.id);
    try {
      await deleteFiling(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotalElements((prev) => Math.max(0, prev - 1));
      setPendingDelete(null);
      setDeleteError("");
    } catch (err) {
      setDeleteError(err.message || "Couldn't delete this filing. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const from = totalElements === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(totalElements, (page + 1) * PAGE_SIZE);

  // A compact window of page-number buttons around the current page, rather
  // than one button per page (which would sprawl once there are many pages).
  const pageButtons = [];
  for (let p = Math.max(0, page - 2); p <= Math.min(totalPages - 1, page + 2); p++) {
    pageButtons.push(p);
  }

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="mb-5">
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Filings
        </div>
        <h1 className="text-[28px] leading-[1.1] mt-1">My Filings</h1>
        <p className="text-[var(--muted)] max-w-[62ch]">Every Form 2290 return across every business, in one place.</p>
      </div>

      <div className="flex flex-wrap gap-3.5 items-end mb-4">
        <FormField label="Search" style={{ flex: "1 1 260px", marginBottom: 0 }}>
          <TextInput
            placeholder="Business name, EIN, return #, or submission ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </FormField>
        <FormField label="Status" style={{ width: 180, marginBottom: 0 }}>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_FILTER_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="From" style={{ width: 165, marginBottom: 0 }}>
          <TextInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </FormField>
        <FormField label="To" style={{ width: 165, marginBottom: 0 }}>
          <TextInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </FormField>
      </div>

      <Panel className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[var(--muted)]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[var(--muted)]">
            {search || status !== "all" || dateFrom || dateTo
              ? "No filings match these filters."
              : "No filings yet — add a business to get started."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[var(--line)]">
                  {["S.No", "E-file No", "Business", "Tax Year", "Status", "PDF", "Schedule 1 PDF", ""].map((h) => (
                    <th
                      key={h}
                      className="text-[12px] font-semibold text-[var(--muted)] uppercase tracking-[0.06em] py-3 px-4 text-left bg-[var(--panel-2)] whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const isDraft = DRAFT_STATUSES.has(row.status);
                  const mergedStatus = getMergedStatus(row);
                  const isRejected = mergedStatus === "rejected";
                  // Rejected returns aren't a draft status (new/half/full)
                  // server-side, but the filer still needs to fix whatever
                  // the IRS flagged and resubmit - so Continue applies here
                  // too, in place of View's read-only look. Delete does NOT:
                  // a rejected return already has an IRS transmission on
                  // record (submissionId/receiptId), so it stays deletable
                  // only up to the point of submission, same as everything
                  // else that's actually reached the IRS.
                  const canContinue = isDraft || isRejected;
                  const canDelete = isDraft;
                  const showView = !isDraft && !isRejected; // submission_with_irs, submitted, accepted
                  const showPdf = mergedStatus === "accepted";
                  // Contextual post-acceptance action, keyed by the payment method chosen in
                  // the Pay step - only shown once there's still a balance owed to the IRS and
                  // that balance is paid out-of-band (eftps/card); efw is auto-debited already.
                  const hasBalanceDue = row.balanceDue != null && row.balanceDue > 0;
                  const payLink = mergedStatus === "accepted" && hasBalanceDue ? payNowLink(row.paymentKind) : null;
                  return (
                    <tr key={row.id} className="border-b border-[var(--line)] last:border-b-0 hover:bg-[var(--accent-soft)] transition-colors">
                      <td className="py-3.5 px-4 text-[14px] text-[var(--muted)]">{page * PAGE_SIZE + i + 1}</td>
                      <td className="py-3.5 px-4 text-[14px] font-mono text-[var(--muted)]">{row.id}</td>
                      <td className="py-3.5 px-4 text-[14px] font-semibold text-[var(--ink)]">
                        {row.businessName || "—"}
                        {row.ein && <div className="text-[12px] font-normal text-[var(--muted)] font-mono">{row.ein}</div>}
                      </td>
                      <td className="py-3.5 px-4 text-[14px] text-[var(--muted)]">{row.year || "—"}</td>
                      <td className="py-3.5 px-4">
                        <StatusBadge status={mergedStatus} />
                        {/* The IRS identifiers for a transmitted return. submissionId
                            exists as soon as it reaches the IRS; receiptId only once the
                            acknowledgement comes back (queueStatus ACK_RECEIVED), so an
                            accepted return shows both and an in-flight one shows just the
                            submission id. Both are what the filer quotes to the IRS. */}
                        {(row.submissionId || row.receiptId) && (
                          <div className="mt-1.5 space-y-0.5 max-w-[240px]">
                            {row.submissionId && (
                              <div className="text-[11px] leading-tight text-[var(--muted)]">
                                <span className="uppercase tracking-wide">Submission ID</span>
                                <div className="font-mono break-all text-[var(--ink)]">{row.submissionId}</div>
                              </div>
                            )}
                            {row.receiptId && (
                              <div className="text-[11px] leading-tight text-[var(--muted)]">
                                <span className="uppercase tracking-wide">Receipt ID</span>
                                <div className="font-mono break-all text-[var(--ink)]">{row.receiptId}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-3.5 px-4">
                        {showPdf ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="!text-xs !px-3 !py-1.5"
                            onClick={() => downloadPdf(row)}
                            disabled={pdfLoadingId === row.id}
                          >
                            {pdfLoadingId === row.id ? "…" : "⬇ Form2290"}
                          </Button>
                        ) : (
                          <span className="text-[13px] text-[var(--muted)]">—</span>
                        )}
                      </td>
                      <td className="py-3.5 px-4">
                        {showPdf && row.schedule1Available ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="!text-xs !px-3 !py-1.5"
                            onClick={() => downloadSchedule1(row)}
                            disabled={sch1LoadingId === row.id}
                          >
                            {sch1LoadingId === row.id ? "…" : "⬇ Schedule 1"}
                          </Button>
                        ) : (
                          <span className="text-[13px] text-[var(--muted)]">
                            {showPdf ? "Pending" : "—"}
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-1.5 justify-end">
                          {payLink && (
                            <a
                              href={payLink.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-[var(--accent)] rounded-[6px] px-3 py-[7px] no-underline hover:opacity-90 whitespace-nowrap"
                              title="Pay your balance due to the IRS - this happens on the IRS's own site, not through us"
                            >
                              {payLink.label} <ExternalLink size={12} />
                            </a>
                          )}
                          {isRejected && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="!text-xs !px-3 !py-1.5"
                              onClick={() => setViewErrorRow(row)}
                            >
                              View Error
                            </Button>
                          )}
                          {canContinue && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="!text-xs !px-3 !py-1.5"
                              onClick={() => openFiling(row)}
                              disabled={openingId === row.id}
                            >
                              {openingId === row.id ? "…" : isRejected ? "Fix & Resubmit →" : "Continue →"}
                            </Button>
                          )}
                          {showView && (
                            <Button variant="ghost" size="sm" className="!text-xs !px-3 !py-1.5" onClick={() => viewFiling(row)}>
                              View
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="!text-xs !px-3 !py-1.5"
                              onClick={() => removeFiling(row)}
                              disabled={deletingId === row.id}
                            >
                              {deletingId === row.id ? "Deleting…" : "Delete"}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {!loading && totalElements > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <div className="text-[13px] text-[var(--muted)]">
            {from}–{to} of {totalElements} filing{totalElements === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="!text-xs !px-3 !py-1.5"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Previous
            </Button>
            {pageButtons.map((p) => (
              <Button
                key={p}
                variant={p === page ? "primary" : "ghost"}
                size="sm"
                className="!text-xs !px-3 !py-1.5 !min-w-[36px]"
                onClick={() => setPage(p)}
              >
                {p + 1}
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              className="!text-xs !px-3 !py-1.5"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!pendingDelete}
        title="Delete Confirmation"
        confirmLabel="Delete"
        loading={deletingId === pendingDelete?.id}
        error={deleteError}
        onConfirm={confirmDeleteFiling}
        onCancel={cancelDeleteFiling}
      >
        Are you sure you want to delete this record? This action cannot be undone.
      </ConfirmModal>

      {viewErrorRow && (
        <RejectionErrorModal
          row={viewErrorRow}
          onClose={() => setViewErrorRow(null)}
          dispatch={dispatch}
          showToast={showToast}
        />
      )}

      {ratePrompt && (
        <FeedbackPanel
          key={ratePrompt.returnId}
          open
          returnId={ratePrompt.returnId}
          businessName={ratePrompt.businessName}
          refCode={refCode}
          onClose={() => setRatePrompt(null)}
        />
      )}
    </div>
  );
};

export default MyFilings;
